// src/lib/DataBuyerService.js
import { DEFAULT_URL } from "./ApiConfig";

/**
 * Service for handling databuyer-related API operations
 */
const DataBuyerService = {
  /**
   * Gets databuyer information by referrer code
   * 
   * @param {string} referrer - The utm_referrer parameter value
   * @param {string} firebaseToken - Optional Firebase authentication token
   * @returns {Promise<Object|null>} - Databuyer data or null if not found
   */
  getDataBuyerByReferrer: async (referrer, firebaseToken = null) => {
    try {
      if (!referrer || referrer.trim() === '') {
        console.warn('No referrer provided to getDataBuyerByReferrer');
        return null;
      }

      const API_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const url = `${API_URL}/api/databuyers/by-referrer?referrer=${encodeURIComponent(referrer)}`;

      const headers = {
        "Content-Type": "application/json",
      };

      const response = await fetch(url, {
        method: "GET",
        headers: headers,
      });

      if (response.status === 404) {
        console.log(`No databuyer found for referrer: ${referrer}`);
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to fetch databuyer for referrer ${referrer}: ${response.status}`
        );
      }

      const dataBuyer = await response.json();
      
      // Validate required fields
      if (!dataBuyer.id) {
        console.warn('Databuyer response missing required id field');
        return null;
      }

      return dataBuyer;

    } catch (error) {
      console.error('Error in getDataBuyerByReferrer:', error);
      // Don't throw - return null to allow graceful degradation
      return null;
    }
  },

  /**
   * Validates a databuyer object has required fields
   * 
   * @param {Object} dataBuyer - The databuyer object to validate
   * @returns {boolean} - True if valid, false otherwise
   */
  isValidDataBuyer: (dataBuyer) => {
    if (!dataBuyer || typeof dataBuyer !== 'object') {
      return false;
    }

    // Required fields
    const requiredFields = ['id', 'name'];
    
    for (const field of requiredFields) {
      if (!dataBuyer[field]) {
        console.warn(`Databuyer missing required field: ${field}`);
        return false;
      }
    }

    return true;
  },

  /**
   * Gets the logo URL for a databuyer, with fallback handling
   * 
   * @param {Object} dataBuyer - The databuyer object
   * @returns {string|null} - Logo URL or null if not available
   */
  getLogoUrl: (dataBuyer) => {
    if (!dataBuyer) {
      return null;
    }

    // Check for smalllogo first, then fallback to logo
    return dataBuyer.listLogo || dataBuyer.logo || null;
  },



  /**
   * Gets display configuration for a databuyer
   * 
   * @param {Object} dataBuyer - The databuyer object
   * @param {string} referrer - The referrer code to determine DIMO enablement
   * @returns {Object} - Configuration object
   */
  getDisplayConfig: (dataBuyer, referrer) => {
    // console.log('=== getDisplayConfig called ===');
    // console.log('dataBuyer:', dataBuyer);
    // console.log('referrer:', referrer);
    
    const enableDimo = referrer === 'dimo';
    if (!dataBuyer) {
      //console.log('No dataBuyer - returning defaults with undefined partnerName');
      return {
        enableDimo,
        showDimoInSummary: enableDimo,
        partnerName: "Ownli",
        logoUrl: "/logos/ownli_logo.png"
      };
    }

    //console.log('Has dataBuyer - returning partner data');
    return {
      enableDimo,
      showDimoInSummary: enableDimo,
      partnerName: dataBuyer.name || undefined, // Changed from 'Ownli' to undefined
      logoUrl: DataBuyerService.getLogoUrl(dataBuyer)
    };
  }
};

export default DataBuyerService;
